import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, BACKEND_URL } from '../../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Loader2, CheckCircle, XCircle, AlertCircle, FileText, Download, Eye, Clock } from 'lucide-react';

const FaultListReview = () => {
  const [pendingLists, setPendingLists] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedList, setSelectedList] = useState(null);
  const [detailedList, setDetailedList] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [reviewData, setReviewData] = useState({
    action: 'approve',
    approved_fault_count: 0,
    admin_notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [listsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/pending-fault-lists`),
        axios.get(`${API}/admin/fault-list-stats`)
      ]);
      setPendingLists(listsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Kon nie data laai nie');
    } finally {
      setLoading(false);
    }
  };

  const fetchDetailedList = async (listId) => {
    setLoadingDetail(true);
    try {
      const response = await axios.get(`${API}/admin/fault-list/${listId}`);
      setDetailedList(response.data);
      setReviewData({
        action: 'approve',
        approved_fault_count: response.data.total_faults,
        admin_notes: ''
      });
    } catch (error) {
      toast.error('Kon nie foute lys laai nie');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleReview = async () => {
    if (!detailedList) return;
    
    setReviewing(true);
    try {
      const response = await axios.post(`${API}/admin/fault-list/${detailedList.id}/review`, reviewData);
      toast.success(response.data.message);
      setSelectedList(null);
      setDetailedList(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon nie foute lys verwerk nie');
    } finally {
      setReviewing(false);
    }
  };

  const getFaultCategoryBadge = (category) => {
    if (category === 'Vertaling') {
      return <Badge className="bg-purple-100 text-purple-700">Vertaling Fout</Badge>;
    }
    return <Badge className="bg-blue-100 text-blue-700">Oorspronklik</Badge>;
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Foute Lys Goedkeuring
        </h2>
        <p className="text-gray-600">Hersien en keur proeflees foute lyste goed</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Clock className="h-8 w-8 text-amber-600" />
              <div>
                <p className="text-sm text-amber-700">Wag vir Goedkeuring</p>
                <p className="text-2xl font-bold text-amber-800">{stats.pending_review || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-8 w-8 text-green-600" />
              <div>
                <p className="text-sm text-gray-500">Goedgekeur</p>
                <p className="text-2xl font-bold">{stats.approved || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-8 w-8 text-orange-600" />
              <div>
                <p className="text-sm text-gray-500">Gedeeltelik</p>
                <p className="text-2xl font-bold">{stats.partially_approved || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <XCircle className="h-8 w-8 text-red-600" />
              <div>
                <p className="text-sm text-gray-500">Afgekeur</p>
                <p className="text-2xl font-bold">{stats.rejected || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Lists */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Foute Lyste Wag vir Goedkeuring
          </CardTitle>
        </CardHeader>
        <CardContent>
          {pendingLists.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-300" />
              <p className="font-medium">Geen foute lyste wag vir goedkeuring nie</p>
            </div>
          ) : (
            <div className="space-y-4">
              {pendingLists.map((list) => (
                <div 
                  key={list.id} 
                  className="p-4 border rounded-lg hover:border-orange-200 transition-colors"
                  data-testid={`fault-list-${list.id}`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg">{list.book_name}</h3>
                      <p className="text-gray-600 text-sm">deur {list.book_author}</p>
                      <div className="flex flex-wrap gap-3 mt-2 text-sm">
                        <span className="text-gray-500">
                          Proefleser: <span className="font-medium">{list.proofreader_name}</span>
                        </span>
                        <span className="text-orange-600 font-medium">
                          {list.fault_count} foute
                        </span>
                        <span className="text-green-600 font-medium">
                          R{list.potential_payment} potensieel
                        </span>
                        {list.has_translator && (
                          <Badge variant="outline" className="text-purple-600">Vertaling</Badge>
                        )}
                      </div>
                      <p className="text-gray-400 text-xs mt-1">
                        Ingedien: {new Date(list.submission_date).toLocaleString('af-ZA')}
                      </p>
                    </div>
                    <Button
                      onClick={() => { setSelectedList(list); fetchDetailedList(list.id); }}
                      className="bg-orange-500 hover:bg-orange-600"
                      data-testid={`review-list-${list.id}`}
                    >
                      <Eye className="h-4 w-4 mr-2" /> Hersien
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Review Dialog */}
      <Dialog open={!!selectedList} onOpenChange={(open) => { if (!open) { setSelectedList(null); setDetailedList(null); } }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Hersien Foute Lys</DialogTitle>
          </DialogHeader>
          
          {loadingDetail ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
            </div>
          ) : detailedList && (
            <div className="space-y-6">
              {/* Book Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-semibold text-lg">{detailedList.book?.name}</h3>
                    <p className="text-gray-600">deur {detailedList.book?.author}</p>
                    <p className="text-sm text-gray-500 mt-1">
                      Proefleser: {detailedList.proofreader?.name} ({detailedList.proofreader?.email})
                    </p>
                  </div>
                  {detailedList.book?.epub_url && (
                    <Button
                      variant="outline"
                      onClick={() => window.open(detailedList.book.epub_url, '_blank')}
                    >
                      <Download className="h-4 w-4 mr-2" /> Laai Boek Af
                    </Button>
                  )}
                </div>
              </div>

              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-3xl font-bold text-orange-600">{detailedList.total_faults}</p>
                    <p className="text-sm text-gray-500">Totale Foute</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-3xl font-bold text-green-600">R{detailedList.potential_payment}</p>
                    <p className="text-sm text-gray-500">Potensiële Betaling</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-3xl font-bold text-blue-600">R2</p>
                    <p className="text-sm text-gray-500">Per Fout</p>
                  </CardContent>
                </Card>
              </div>

              {/* Fault Entries */}
              <div>
                <h4 className="font-semibold mb-3">Foute Lys ({detailedList.total_faults})</h4>
                <div className="max-h-80 overflow-y-auto space-y-3 border rounded-lg p-3">
                  {detailedList.fault_entries?.map((fault, idx) => (
                    <div key={idx} className="p-3 bg-white border rounded shadow-sm">
                      <div className="flex justify-between items-start mb-2">
                        <span className="font-medium text-gray-700">Fout #{idx + 1}</span>
                        <div className="flex gap-2">
                          {getFaultCategoryBadge(fault.fout_kategorie)}
                          <Badge variant="outline">{fault.fout_tipe}</Badge>
                        </div>
                      </div>
                      <div className="grid grid-cols-4 gap-2 text-sm text-gray-500 mb-2">
                        {fault.hoofstuk && <span>Hoofstuk: {fault.hoofstuk}</span>}
                        <span>Bladsy: {fault.bladsy}</span>
                        <span>Paragraaf: {fault.paragraaf}</span>
                        <span>Sin: {fault.sin}</span>
                      </div>
                      <div className="space-y-1 text-sm">
                        <p><span className="text-red-600 font-medium">Oorspronklik:</span> {fault.oorspronklike_teks}</p>
                        <p><span className="text-green-600 font-medium">Regstelling:</span> {fault.voorgestelde_regstelling}</p>
                        {fault.nota && <p><span className="text-gray-500">Nota:</span> {fault.nota}</p>}
                      </div>
                      {fault.screenshot_url && (
                        <div className="mt-2">
                          <img src={fault.screenshot_url} alt="Screenshot" className="max-h-32 rounded border" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Review Actions */}
              <div className="border-t pt-4 space-y-4">
                <h4 className="font-semibold">Goedkeuring Aksie</h4>
                
                <div className="flex gap-4">
                  <Button
                    variant={reviewData.action === 'approve' ? 'default' : 'outline'}
                    onClick={() => setReviewData({ ...reviewData, action: 'approve', approved_fault_count: detailedList.total_faults })}
                    className={reviewData.action === 'approve' ? 'bg-green-600 hover:bg-green-700' : ''}
                  >
                    <CheckCircle className="h-4 w-4 mr-2" /> Keur Alles Goed
                  </Button>
                  <Button
                    variant={reviewData.action === 'partial' ? 'default' : 'outline'}
                    onClick={() => setReviewData({ ...reviewData, action: 'partial' })}
                    className={reviewData.action === 'partial' ? 'bg-orange-600 hover:bg-orange-700' : ''}
                  >
                    <AlertCircle className="h-4 w-4 mr-2" /> Gedeeltelik
                  </Button>
                  <Button
                    variant={reviewData.action === 'reject' ? 'default' : 'outline'}
                    onClick={() => setReviewData({ ...reviewData, action: 'reject', approved_fault_count: 0 })}
                    className={reviewData.action === 'reject' ? 'bg-red-600 hover:bg-red-700' : ''}
                  >
                    <XCircle className="h-4 w-4 mr-2" /> Keur Af
                  </Button>
                </div>

                {reviewData.action === 'partial' && (
                  <div>
                    <Label>Aantal Goedgekeurde Foute</Label>
                    <Input
                      type="number"
                      min="0"
                      max={detailedList.total_faults}
                      value={reviewData.approved_fault_count}
                      onChange={(e) => setReviewData({ ...reviewData, approved_fault_count: parseInt(e.target.value) || 0 })}
                      className="max-w-xs"
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      Betaling: R{reviewData.approved_fault_count * 2}
                    </p>
                  </div>
                )}

                <div>
                  <Label>Admin Notas (Opsioneel)</Label>
                  <Textarea
                    value={reviewData.admin_notes}
                    onChange={(e) => setReviewData({ ...reviewData, admin_notes: e.target.value })}
                    placeholder="Voeg enige notas of rede vir besluit by..."
                    rows={2}
                  />
                </div>

                {/* Summary */}
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm">
                    <strong>Opsomming:</strong> {' '}
                    {reviewData.action === 'approve' && `Alle ${detailedList.total_faults} foute sal goedgekeur word. Betaling: R${detailedList.total_faults * 2}`}
                    {reviewData.action === 'partial' && `${reviewData.approved_fault_count} van ${detailedList.total_faults} foute sal goedgekeur word. Betaling: R${reviewData.approved_fault_count * 2}`}
                    {reviewData.action === 'reject' && 'Alle foute sal afgekeur word. Geen betaling nie.'}
                  </p>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => { setSelectedList(null); setDetailedList(null); }}>
                  Kanselleer
                </Button>
                <Button
                  onClick={handleReview}
                  disabled={reviewing}
                  className={
                    reviewData.action === 'approve' ? 'bg-green-600 hover:bg-green-700' :
                    reviewData.action === 'reject' ? 'bg-red-600 hover:bg-red-700' :
                    'bg-orange-600 hover:bg-orange-700'
                  }
                >
                  {reviewing ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Besig...</>
                  ) : (
                    'Bevestig Besluit'
                  )}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FaultListReview;
